<template>
    <PrimeVueSelect
        v-bind="primeVueProps"
        :options="computedOptions"
        :model-value="coercedModelValue"
        @update:modelValue="$emit('update:modelValue', $event)"
    >
        <!-- Pass through all PrimeVue slots (except default) -->
        <template v-for="(_, slot) in filteredSlots" #[slot]="slotProps">
            <slot :name="slot" v-bind="slotProps" />
        </template>
    </PrimeVueSelect>
</template>

<script>
import PrimeVueSelect from 'primevue/select';
/**
 * ESelect - Abstraction wrapper around PrimeVue's Select
 *
 * Usage with slots (Buefy style):
 *   <e-select v-model="selected" placeholder="Select an option">
 *     <option value="1">Option 1</option>
 *     <option value="2">Option 2</option>
 *     <option value="3">Option 3</option>
 *   </e-select>
 *
 * Usage with options array (PrimeVue style):
 *   <e-select
 *     v-model="selected"
 *     :options="cities"
 *     optionLabel="name"
 *     optionValue="id"
 *     placeholder="Select a city"
 *   />
 *
 * All Buefy b-select and PrimeVue Select props, events, and slots are supported.
 */
export default {
    name: 'ESelect',
    components: {
        PrimeVueSelect,
    },
    inheritAttrs: false,
    props: {
        // PrimeVue-specific props
        options: {
            type: Array,
            default: null,
        },
        optionLabel: {
            type: [String, Function],
            default: null,
        },
        optionValue: {
            type: [String, Function],
            default: null,
        },
        modelValue: {
            type: null,
            default: undefined,
        },
    },
    emits: ['update:modelValue'],
    computed: {
        /**
         * Computed options that combines explicit options prop with extracted options from slots
         */
        computedOptions() {
            return this.options || this.extractOptionsFromSlot() || [];
        },
        /**
         * modelValue coerced to match the numeric-string-to-number conversion applied to
         * slot-extracted option values, so a saved value hydrated as a string (e.g. "12")
         * still matches its corresponding option (12) instead of rendering the placeholder.
         * Scoped to slot-extraction mode only, matching extractOptionsFromSlot()'s own scope.
         */
        coercedModelValue() {
            if (this.options) {
                return this.modelValue;
            }

            const value = this.modelValue;

            if (typeof value === 'string' && value !== '' && !isNaN(value)) {
                return Number(value);
            }

            return value;
        },
        /**
         * Filter out the default slot when passing slots to PrimeVue
         * since we're extracting options from it
         */
        filteredSlots() {
            if (!this.$slots) {
                return {};
            }
            const { default: defaultSlot, ...rest } = this.$slots;
            return this.options ? this.$slots : rest;
        },
        primeVueProps() {
            const props = { ...this.$attrs };

            // Add PrimeVue-specific props if provided
            if (this.optionLabel) {
                props.optionLabel = this.optionLabel;
            } else if (!this.options) {
                // If extracting from slots, use our label/value structure
                props.optionLabel = 'label';
                props.optionValue = 'value';
            }

            if (this.optionValue) {
                props.optionValue = this.optionValue;
            }

            // Make PrimeVue Select expand to full width by default
            // unless explicitly set to false
            if (props.fluid === undefined) {
                props.fluid = true;
            }

            return props;
        },
    },
    methods: {
        /**
         * Extract options from slot-based <option> elements when options array is not provided
         * This ensures backward compatibility with existing Buefy-style selects
         * NOTE: This is a method (not computed) to avoid infinite reactivity loops
         * when slots contain v-for with reactive data
         */
        extractOptionsFromSlot() {
            // If options array is explicitly provided, don't extract
            if (this.options) {
                return null;
            }

            // Only extract if we have default slot content
            if (!this.$slots.default) {
                return null;
            }

            try {
                // Get the VNodes from the default slot
                const vnodes = this.$slots.default();
                const extracted = [];
                const seen = new WeakSet();

                // Helper function to process a VNode recursively
                const processVNode = vnode => {
                    // Guard against circular references
                    if (!vnode || typeof vnode !== 'object') {
                        return;
                    }
                    if (seen.has(vnode)) {
                        return;
                    }
                    seen.add(vnode);

                    // Handle option elements
                    if (vnode.type === 'option') {
                        const props = vnode.props || {};
                        let { value } = props;

                        // Convert numeric string values to numbers to match modelValue types
                        // This handles cases where object keys (always strings) are used as option values
                        if (typeof value === 'string' && value !== '' && !isNaN(value)) {
                            value = Number(value);
                        }

                        // Extract text content from children
                        let label = '';
                        if (vnode.children) {
                            if (typeof vnode.children === 'string') {
                                label = vnode.children;
                            } else if (Array.isArray(vnode.children)) {
                                // Concatenate text from child nodes
                                label = vnode.children
                                    .map(child => (typeof child === 'string' ? child : child?.children || ''))
                                    .join('');
                            }
                        }

                        extracted.push({
                            label: label.trim() || value,
                            value,
                        });
                    }
                    // Recursively process children (but not for options - they've been handled)
                    else if (Array.isArray(vnode.children)) {
                        vnode.children.forEach(child => {
                            if (child && typeof child === 'object') {
                                processVNode(child);
                            }
                        });
                    }
                };

                // Process all VNodes
                vnodes.forEach(vnode => processVNode(vnode));

                return extracted.length > 0 ? extracted : null;
            } catch (error) {
                console.warn('ESelect: Failed to extract options from slot', error);
                return null;
            }
        },
    },
};
</script>

<style lang="scss">
@import '~assets/scss/toolbox.scss';

.p-select {
    --p-select-border-color: var(--border);

    height: 3rem;
    align-items: center;

    .p-select-label {
        padding: 0.375em 2.5em 0.375em 0.75em;
        font-size: 1rem;
    }

    .p-select-dropdown {
        width: 2.5em;
        right: 0;
    }
}

.p-select-overlay {
    .p-select-list {
        padding: 0;
    }

    .p-select-option {
        padding: 0.5rem 0.75rem;
    }
}
</style>
